import { Injectable } from '@angular/core';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalConfirmComponent } from './modal/modal-confirm.component';

/**
 * This based on on our own angularJS service $enhandedHttp which was an enhancement of the $http service.
 * It has been renamed with inspiration from https://github.com/m-hassan-tariq/RestfulWebserviceForAngular2UsingPromises
 *
 * It does not support shorthand methods like get(), post(), etc.
 * It takes a config object like $http that may include extra settings.
 * If passed a loadingIndicator object it will also increase/decrease a reference count and loading state.
 * This indicator object can then be used in the view e.g. to display a loading indicator.
 * The default is that this service will display error messages received from the backend, if any.
 * It can also be configured with a specific error message to be displayed on errors or
 * to disable the error dialog from this service and the caller will then have to deal with
 * the error when the response promise is rejected.
 *
 * This is the simple use:
 * request( 'get', url: '/some/endpoint/', { headers : {'Content-Type':'application/json'} })
 * .then(function(data) { var responseData = data } );
 *
 * This will make the request and when the promise is resolved (http request successful) the data
 * is returned in the success function. On errors the service will display an error dialog.
 *
 * The backend should respond e.g. with a Server Error (status 500) and the error message in the reponse.
 * Either as raw text or preferably in a JSON object of the following format:
 *
 *   {"error" : {"responseCode": "someEndpoint123", "response" : "Wrong input format in parameter %1", "parameters" : ["email"]} }
 *
 * This format will allow for using the responseCode to string lookup in a locale dictionary and expanding with given parameters.
 * If no responseCode the given error text can be used or if the caller is handling the error the caller may use the parameters
 * to expand the caller's error message. Parameters can be omitted if no parameters needed.
 *
 * Here is a more elaborate example:
 *
 * request( 'get', url: '/some/endpoint/',
 *                  headers : {'Content-Type':'application/json'},
 *                  errorMessage: 'Failed getting some data from some endpoint',
 *                  disableErrorDialog: true,
 *                  retryBtn : false
 *               }, loadingIndicator
 *         ).then(function(data), function(error) { log(error.response)});
 *
 * This will disable the service from showing a dialog on errors and the caller will just log the error message.
 * A loading indicator object is passed to the service and loadingIndicator.state is true while waiting for response
 * and false when the promise is resolved or rejected.
 *
 */

@Injectable()
export class BackendAPIService {

    constructor(private http: HttpClient,
                private modalService: BsModalService
    ) {}

    private static updateLoadingIndicator(loadingIndicator, increment: number): void {
        if (loadingIndicator !== undefined) {
            loadingIndicator.refCount += increment;
            loadingIndicator.state = loadingIndicator.refCount > 0;
        }
        // NOTE: For debugging purposes we do NOT check for uninitialized object (e.g. state missing)
        //       This way we'll see such errors in the console (while refactoring code to use this service)
    }

    request(method: string, url: string, config, loadingIndicator?): Promise<any> {

        BackendAPIService.updateLoadingIndicator(loadingIndicator, +1);

        // Add parameters to config to use for retry on errors
        config.method = method;
        config.url = url;
        config.loadingIndicator = loadingIndicator;

        let body = {};

        if (config.body !== undefined) {
          body = config.body;
        }
      // required so interceptor can get these values
// TODO: Review if these are still used - and if they are we need to refactor to NOT use these in the interceptor!
        (body as any).retryBtn = config.retryBtn;
        (body as any).disableErrorDialog = config.disableErrorDialog;
        (body as any).errorMessage = config.errorMessage;

        return this.http.request(method, url, {
            headers: config.headers,
            body
        })
            .toPromise()
            .then(response => {
                BackendAPIService.updateLoadingIndicator(loadingIndicator, -1);

                return response;
            })
            .catch((error: HttpErrorResponse | any) => {
                BackendAPIService.updateLoadingIndicator(loadingIndicator, -1);

                return this.handleError(error, config);
            });
    }

    private handleError(httpResponse: HttpErrorResponse, config): Promise<any> {
        // TODO: Check if response.error is where the json from the servlet is

        let response = httpResponse.error;
        if (config.returnHttpResponseOnFail) {
            response = httpResponse;
        }

        let errorMessage = config.errorMessage || `Failed getting data from server [${config.url}]`;
        if (httpResponse.error && httpResponse.error.error && httpResponse.error.error.reason !== undefined) {
            errorMessage += ` (${httpResponse.error.error.reason})`;
        }
        //console.error(errorMessage);

        if (config.disableErrorDialog !== undefined && config.disableErrorDialog) {

              // TODO: Should we return the errorMessage as expanded below?
              if (config.shouldRejectOnError !== undefined && config.shouldRejectOnError) {
                  return Promise.reject(response);
              }
          } else {
              if (config.handleStatus !== httpResponse.status) { // TODO: Extend to allow setting a list of status codes
                  const dialogHeader = httpResponse.statusText || 'Error';

                  if (config.retryBtn === undefined || !config.retryBtn) {
                      return this.alert(response, config, dialogHeader, errorMessage);
                  } else {
                    // When user opts for not to retry, we need to return error to calling component
                    return Promise.reject(response);
                  }
              } else {
                  if (config.shouldRejectOnError !== undefined && config.shouldRejectOnError) {
                      return Promise.reject(response); // TODO: Combine the check for handleStatus up to disableErrorDialog=true?
                  }
              }
          }

    }

    private alert(response: any, config, title: string, message: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const modalRef = this.modalService.show(BsModalConfirmComponent,
                {
                    initialState: {
                        title,
                        message
                    }
                });
            modalRef.content.onClose
                .subscribe(_ => {
                    if (config.shouldRejectOnError !== undefined && config.shouldRejectOnError) {
                        reject(response);
                    }
                });
        });

    }
}
